import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorBoundary } from "react-error-boundary";
import { Box, Button, Divider, Stack, Typography } from "@mui/material";
import Panel from "../../../Common/components/Panel";
import {
  convertBooleanLikeStringToBoolean,
  isOlderThanSixMonths,
  convertStringToJSON,
  convertPxToRem,
} from "../../../../utils";
import { HealthMeasurementIdentifier, useGlobalStore } from "../../../../store";
import HealthScoreIndicator from "../HealthScoreIndicator";
import HealthProfileLegendModal from "../HealthProfileLegendModal";
import BodyMassIndexHealthMeasurement from "./components/BodyMassIndexHealthMeasurement";
import {
  healthMeasurementListItemStyle,
  utilityButtonStyle,
} from "../../../../theme";
import HealthMeasurementListElement from "./components/HealthMeasurementListElement";
import IconLoader from "../../../Common/components/IconLoader";
import {
  HealthProfileItem,
  HealthProfileSuccessResponseData,
} from "../../../../services/core-api-adapter";
import BinahScanCallToActionPanel from "../../../Common/components/BinahScanCallToActionPanel";
import {
  FeatureToggleIdentifier,
  isFeatureEnabled,
} from "@/services/feature-toggle-adapter";

function ErrorFallback() {
  const { t } = useTranslation();

  return (
    <Panel role="alert">
      <Typography variant="h2" align="center">
        {t("common.somethingWentWrong")}
      </Typography>
    </Panel>
  );
}

const expiredVerifiedStatusDisplayedForTypes = [
  "BodyMassIndex",
  "Height",
  "Weight",
  "MetabolicAge",
  "BodyFat",
  "BodyWaterPercent",
  "SystolicBloodPressure",
  "BloodType",
  "Pulse",
];

export function getVerifiedStatus(lastUpdated: string | null, type: string) {
  if (
    expiredVerifiedStatusDisplayedForTypes.includes(type) &&
    lastUpdated &&
    isOlderThanSixMonths(new Date(lastUpdated))
  ) {
    return <IconLoader icon="CheckVerifiedFilledIcon" color="secondary" />;
  }
  return <IconLoader icon="CheckVerifiedFilledIcon" color="primary" />;
}

interface HealthProfileProps {
  healthProfile: HealthProfileSuccessResponseData | null;
}

export type DecodedHealthProfileItem = HealthProfileItem & {
  valueAsJSON: unknown;
};

export default function HealthProfile({ healthProfile }: HealthProfileProps) {
  const { t } = useTranslation();
  const { state } = useGlobalStore();
  const [isLegendModalOpened, setIsLegendModalOpened] = useState(false);
  const isTrendedVitalsEnabled = isFeatureEnabled(
    FeatureToggleIdentifier.ENABLE_TRENDED_VITALS
  );

  function onLegendModalClose() {
    setIsLegendModalOpened(false);
  }

  function onLegendModalOpen() {
    setIsLegendModalOpened(true);
  }

  const healthScore: number = Number(healthProfile?.healthScoreValue) || 0;

  const healthProfileData =
    healthProfile &&
    healthProfile.items.reduce(
      (accumulator, item) => {
        accumulator[item.questionType] = {
          ...item,
          valueAsJSON: item.data?.value
            ? convertStringToJSON(item.data.value)
            : null,
        };
        return accumulator;
      },
      {} as Record<string, DecodedHealthProfileItem>
    );

  const smokingStatusValueAndUnit = (
    smokingStatus?: DecodedHealthProfileItem,
    smokingFrequency?: DecodedHealthProfileItem
  ): string | null => {
    const smokingStatusNormalised = String(
      smokingStatus?.valueAsJSON
    ).toLowerCase();

    if (smokingStatusNormalised === "no") {
      return t("HealthProfile.SmokingStatus.no");
    }

    if (
      smokingStatusNormalised === "yes" &&
      smokingFrequency?.data?.interval &&
      smokingFrequency?.valueAsJSON
    ) {
      return t(
        `HealthProfile.SmokingFrequency.${smokingFrequency?.data?.interval}` as any,
        {
          value: smokingFrequency?.valueAsJSON as any,
        }
      );
    }

    if (smokingStatusNormalised === "yes") {
      return t("HealthProfile.SmokingStatus.yes");
    }

    return null;
  };

  const getBloodTypeValue = (
    bloodTypeHealthMeasurementData: DecodedHealthProfileItem
  ) => {
    const bloodTypeValue =
      (typeof bloodTypeHealthMeasurementData?.valueAsJSON === "string" &&
        bloodTypeHealthMeasurementData?.valueAsJSON?.toUpperCase()) ||
      null;

    if (bloodTypeValue === "UNKNOWN") {
      return t("HealthProfile.BloodType.unknown.label");
    }

    return bloodTypeValue;
  };

  const getBloodPressureValue = (
    systolicBloodPressure: DecodedHealthProfileItem,
    diastolicBloodPressure: DecodedHealthProfileItem
  ) => {
    if (
      systolicBloodPressure?.valueAsJSON &&
      diastolicBloodPressure?.valueAsJSON
    ) {
      return (
        systolicBloodPressure.valueAsJSON +
        "/" +
        diastolicBloodPressure.valueAsJSON
      );
    }

    return null;
  };

  const booleanToNaturalLanguage = (value: boolean | null): string | null => {
    if (value === true) {
      return t("common.yes");
    }
    if (value === false) {
      return t("common.no");
    }
    return null;
  };

  function getHealthMeasurementInternalState({
    item,
    valueOverride,
    unitOverride,
    isUserEditable,
    healthMeasurementIdentifier,
  }: {
    item: DecodedHealthProfileItem | null;
    unitOverride: string | null | undefined;
    valueOverride: string | null | undefined;
    isUserEditable: boolean | undefined;
    healthMeasurementIdentifier: HealthMeasurementIdentifier;
  }) {
    const isHealthMeasurementEditSupportedByUI =
      state.healthMeasurementsWithEditView.filter((clickThroughIdentifier) => {
        return clickThroughIdentifier === healthMeasurementIdentifier;
      }).length > 0;

    const isHealthMeasurementDetailSupportedByUI =
      state.healthMeasurementsWithDetailView.filter(
        (clickThroughIdentifier) => {
          return clickThroughIdentifier === healthMeasurementIdentifier;
        }
      ).length > 0;

    const internalState: {
      value: string | null;
      unit: string | null;
      hasEditView: boolean | undefined;
      hasDetailView: boolean | undefined;
    } = {
      value: null,
      unit: null,
      hasEditView: item?.isUserEditable || isUserEditable,
      hasDetailView: isHealthMeasurementDetailSupportedByUI,
    };

    if (item?.data || valueOverride) {
      internalState.value =
        (item?.valueAsJSON as any)?.value || item?.valueAsJSON || null;
      internalState.unit = item?.data?.unit || null;

      if (valueOverride) {
        internalState.value = valueOverride;
      }

      if (unitOverride || unitOverride === "") {
        internalState.unit = unitOverride;
      }

      if (
        internalState.hasEditView === true &&
        isHealthMeasurementEditSupportedByUI === true
      ) {
        internalState.hasEditView = true;
      } else {
        internalState.hasEditView = false;
      }
    }
    return internalState;
  }

  function healthMeasurementElementOrNull({
    healthMeasurementIdentifier,
    unitOverride,
    valueOverride,
    shouldDisplayEvenIfValueIsNull = true,
    healthRiskIndicator,
    isUserEditable = false,
    isVerified,
    lastUpdated,
  }: {
    healthMeasurementIdentifier: HealthMeasurementIdentifier;
    unitOverride?: string | null;
    valueOverride?: string | null;
    shouldDisplayEvenIfValueIsNull?: boolean;
    healthRiskIndicator?: string;
    isUserEditable?: boolean;
    isVerified?: boolean;
    lastUpdated?: string;
  }) {
    const item = healthProfileData?.[healthMeasurementIdentifier] ?? null;

    const internalState = getHealthMeasurementInternalState({
      item,
      healthMeasurementIdentifier,
      isUserEditable,
      unitOverride,
      valueOverride,
    });

    const formattedInternalValue = internalState.unit
      ? `${internalState.value} ${internalState.unit}`
      : internalState.value;

    const component = (
      <HealthMeasurementListElement
        healthMeasurementIdentifier={healthMeasurementIdentifier}
        hasEditView={internalState.hasEditView}
        hasDetailView={internalState.hasDetailView}
        data={
          internalState.value ? formattedInternalValue : internalState.value
        }
        isVerified={isVerified || !!item?.isVerified}
        name={t(`HealthProfile.${healthMeasurementIdentifier}.name` as any)}
        healthRiskIndicator={
          healthRiskIndicator || item?.healthRiskIndicator || ""
        }
        lastUpdated={lastUpdated || item?.lastUpdated || null}
      />
    );

    if (
      internalState.value !== null ||
      (internalState.value === null && shouldDisplayEvenIfValueIsNull)
    ) {
      return component;
    }

    return null;
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      {healthProfileData && (
        <Stack spacing={2}>
          <HealthProfileLegendModal
            isOpen={isLegendModalOpened}
            onClose={onLegendModalClose}
          />

          {isTrendedVitalsEnabled ? (
            <Panel>
              <Stack spacing={3} py={1}>
                <Stack spacing={1}>
                  <Typography variant="h4">
                    {t("MyHealth.profileHealthScoreCard.title")}
                  </Typography>
                  <Typography variant="body2">
                    {t("MyHealth.profileHealthScoreCard.description")}
                  </Typography>
                </Stack>
                <Button href="/home/health-score">
                  {t("MyHealth.profileHealthScoreCard.button")}
                </Button>
              </Stack>
            </Panel>
          ) : (
            <>
              <Panel overflow="unset">
                <HealthScoreIndicator healthScore={healthScore} />
              </Panel>

              <BinahScanCallToActionPanel />
            </>
          )}

          <Panel>
            <Stack spacing={1} divider={<Divider />}>
              <Stack
                direction={"row"}
                justifyContent="space-between"
                alignItems={"center"}
              >
                <Box py={1}>
                  <Typography variant="h5" alignItems="center">
                    {t("HealthProfile.legend.verifiedData")}
                  </Typography>
                  <Typography variant="subtitle2">
                    {healthProfile?.lastUpdatedAt?.split("T")[0]}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  color="neutral"
                  variant="outlined"
                  onClick={onLegendModalOpen}
                  startIcon={
                    <IconLoader
                      icon="InformationSymbolInCircleIcon"
                      sx={{ fontSize: convertPxToRem(12) }}
                    />
                  }
                  sx={utilityButtonStyle}
                >
                  <Typography variant="body2" fontWeight={600}>
                    {t("HealthProfile.legend.info")}
                  </Typography>
                </Button>
              </Stack>
              <Stack
                component="ul"
                aria-label={t("HealthProfile.HealthMeasurementList.title")}
                spacing={1}
                divider={<Divider />}
              >
                {healthMeasurementElementOrNull({
                  healthMeasurementIdentifier:
                    HealthMeasurementIdentifier.SexAtBirth,
                })}

                {healthMeasurementElementOrNull({
                  healthMeasurementIdentifier:
                    HealthMeasurementIdentifier.DateOfBirth,
                })}

                {healthMeasurementElementOrNull({
                  healthMeasurementIdentifier: HealthMeasurementIdentifier.Age,
                  shouldDisplayEvenIfValueIsNull: false,
                })}

                {healthMeasurementElementOrNull({
                  healthMeasurementIdentifier:
                    HealthMeasurementIdentifier.MetabolicAge,
                  shouldDisplayEvenIfValueIsNull: false,
                })}

                <Stack>
                  <Stack {...healthMeasurementListItemStyle}>
                    <BodyMassIndexHealthMeasurement
                      healthProfileData={healthProfileData}
                    />
                  </Stack>
                  <Stack pl={1} pt={1} spacing={1}>
                    <Typography variant="body2">
                      {t("HealthProfile.BodyMassIndex.description")}
                    </Typography>

                    {healthMeasurementElementOrNull({
                      healthMeasurementIdentifier:
                        HealthMeasurementIdentifier.Weight,
                      unitOverride: "kg",
                    })}

                    {healthMeasurementElementOrNull({
                      healthMeasurementIdentifier:
                        HealthMeasurementIdentifier.Height,
                      unitOverride: "cm",
                    })}
                  </Stack>
                </Stack>

                {healthMeasurementElementOrNull({
                  healthMeasurementIdentifier:
                    HealthMeasurementIdentifier.BodyFat,
                  unitOverride: "",
                  shouldDisplayEvenIfValueIsNull: false,
                })}

                {healthMeasurementElementOrNull({
                  healthMeasurementIdentifier:
                    HealthMeasurementIdentifier.BodyWaterPercent,
                  unitOverride: "",
                  shouldDisplayEvenIfValueIsNull: false,
                })}

                {healthMeasurementElementOrNull({
                  healthMeasurementIdentifier:
                    HealthMeasurementIdentifier.BloodPressure,
                  valueOverride: getBloodPressureValue(
                    healthProfileData["SystolicBloodPressure"],
                    healthProfileData["DiastolicBloodPressure"]
                  ),
                  isVerified:
                    healthProfileData["SystolicBloodPressure"]?.isVerified ||
                    healthProfileData["DiastolicBloodPressure"]?.isVerified,
                  lastUpdated:
                    healthProfileData["SystolicBloodPressure"]?.lastUpdated ||
                    healthProfileData["DiastolicBloodPressure"]?.lastUpdated ||
                    undefined,
                  healthRiskIndicator:
                    healthProfileData["SystolicBloodPressure"]
                      ?.healthRiskIndicator ||
                    healthProfileData["DiastolicBloodPressure"]
                      ?.healthRiskIndicator ||
                    undefined,
                  isUserEditable: false,
                  shouldDisplayEvenIfValueIsNull: false,
                })}

                {healthMeasurementElementOrNull({
                  healthMeasurementIdentifier:
                    HealthMeasurementIdentifier.BloodType,
                  valueOverride: getBloodTypeValue(
                    healthProfileData["BloodType"]
                  ),
                })}

                {healthMeasurementElementOrNull({
                  healthMeasurementIdentifier:
                    HealthMeasurementIdentifier.Pulse,
                  unitOverride: "",
                  shouldDisplayEvenIfValueIsNull: false,
                })}

                {healthMeasurementElementOrNull({
                  healthMeasurementIdentifier:
                    HealthMeasurementIdentifier.SaturationOfPeripheralOxygen,
                  unitOverride: "",
                  shouldDisplayEvenIfValueIsNull: false,
                })}

                {healthMeasurementElementOrNull({
                  healthMeasurementIdentifier:
                    HealthMeasurementIdentifier.BreathingRate,
                  shouldDisplayEvenIfValueIsNull: false,
                })}

                {healthMeasurementElementOrNull({
                  healthMeasurementIdentifier:
                    HealthMeasurementIdentifier.SmokingStatusAndFrequency,
                  unitOverride: "",
                  valueOverride: smokingStatusValueAndUnit(
                    healthProfileData["SmokingStatus"],
                    healthProfileData["SmokingFrequency"]
                  ),
                  healthRiskIndicator:
                    healthProfileData["SmokingStatus"]?.healthRiskIndicator ??
                    undefined,
                  isUserEditable:
                    healthProfileData["SmokingStatus"]?.isUserEditable,
                })}

                {healthMeasurementElementOrNull({
                  healthMeasurementIdentifier:
                    HealthMeasurementIdentifier.ConditionsStatusAndList,
                  unitOverride: "",
                  valueOverride: booleanToNaturalLanguage(
                    convertBooleanLikeStringToBoolean(
                      healthProfileData["ConditionsStatus"]
                        ?.valueAsJSON as string
                    )
                  ),
                  healthRiskIndicator:
                    healthProfileData["ConditionsStatus"]
                      ?.healthRiskIndicator ?? undefined,
                  isUserEditable:
                    healthProfileData["ConditionsStatus"]?.isUserEditable,
                })}

                {healthMeasurementElementOrNull({
                  healthMeasurementIdentifier:
                    HealthMeasurementIdentifier.AllergiesStatusAndList,
                  unitOverride: "",
                  valueOverride: booleanToNaturalLanguage(
                    convertBooleanLikeStringToBoolean(
                      healthProfileData["AllergiesStatus"]
                        ?.valueAsJSON as string
                    )
                  ),
                  healthRiskIndicator:
                    healthProfileData["AllergiesStatus"]?.healthRiskIndicator ??
                    undefined,
                  isUserEditable:
                    healthProfileData["AllergiesStatus"]?.isUserEditable,
                })}
              </Stack>
            </Stack>
          </Panel>
        </Stack>
      )}
    </ErrorBoundary>
  );
}
